import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Star, Award, Heart, MapPin, Briefcase, Send, ChevronDown, ChevronUp, Target, CheckCircle } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const RecommendedSalespeople = ({ postId, postTitle, token }) => {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [invitingId, setInvitingId] = useState(null);

  const fetchRecommendations = useCallback(async () => {
    if (!postId) return;
    try {
      setLoading(true);
      const res = await fetch(`${API}/business/posts/${postId}/recommended?limit=8`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setRecommendations(data);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    } finally {
      setLoading(false);
    }
  }, [postId, token]);

  useEffect(() => { fetchRecommendations(); }, [fetchRecommendations]);

  const handleInvite = async (e, sp) => {
    e.stopPropagation();
    try {
      setInvitingId(sp.id);
      const res = await fetch(`${API}/business/invite/${sp.id}?post_id=${postId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to send invite');
      }
      toast.success(`Invite sent to ${sp.name}`);
      setRecommendations(prev => prev.map(r =>
        r.id === sp.id ? { ...r, invited: true } : r
      ));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setInvitingId(null);
    }
  };

  const handleShortlist = async (e, sp) => {
    e.stopPropagation();
    try {
      const method = sp.shortlisted ? 'DELETE' : 'POST';
      const res = await fetch(`${API}/business/shortlist/${sp.id}`, {
        method,
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed');
      setRecommendations(prev => prev.map(r =>
        r.id === sp.id ? { ...r, shortlisted: !r.shortlisted } : r
      ));
    } catch (error) {
      toast.error('Failed to update shortlist');
    }
  };

  const badgeConfig = {
    GOLD: { label: 'Gold', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' },
    SILVER: { label: 'Silver', color: 'text-slate-300 bg-slate-300/10 border-slate-300/30' },
    BRONZE: { label: 'Bronze', color: 'text-amber-600 bg-amber-600/10 border-amber-600/30' }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-white/10 bg-[#111317] p-5" data-testid="recommended-sp-widget">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-5 h-5 text-brand-gold" />
          <span className="font-semibold text-brand-text">Recommended Salespeople</span>
        </div>
        <div className="text-sm text-brand-muted">Finding matches...</div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-white/10 bg-[#111317] p-5" data-testid="recommended-sp-widget">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full mb-1"
        data-testid="recommended-sp-toggle"
      >
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-brand-gold" />
          <span className="font-semibold text-brand-text">Recommended Salespeople</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-brand-gold/10 text-brand-gold">{recommendations.length}</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-brand-muted" /> : <ChevronDown className="w-4 h-4 text-brand-muted" />}
      </button>
      {recommendations.length > 0 && (
        <p className="text-xs text-brand-muted mb-3">Based on industry, region, skills, and reputation</p>
      )}

      {expanded && (
        recommendations.length === 0 ? (
          <p className="text-sm text-brand-muted py-4">No matching salespeople found for this post</p>
        ) : (
          <div className="space-y-2">
            {recommendations.map((sp, idx) => {
              const b = sp.badge ? badgeConfig[sp.badge] : null;
              return (
                <div
                  key={sp.id}
                  data-testid="recommended-sp-card"
                  onClick={() => navigate(`/salespeople/${sp.id}`)}
                  className="group flex items-center gap-3 p-3 rounded-lg bg-brand-bg border border-white/5 hover:border-brand-gold/30 cursor-pointer transition-colors"
                >
                  {/* Rank */}
                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-brand-gold text-black' :
                    idx === 1 ? 'bg-gray-400 text-black' :
                    idx === 2 ? 'bg-amber-700 text-white' :
                    'bg-zinc-800 text-brand-muted'
                  }`}>
                    {idx + 1}
                  </div>

                  {/* Avatar */}
                  {sp.avatar ? (
                    <img src={sp.avatar} alt={sp.name} className="w-10 h-10 rounded-full object-cover border border-white/10 flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center text-brand-gold font-semibold flex-shrink-0">
                      {sp.name?.charAt(0)?.toUpperCase()}
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-brand-text truncate group-hover:text-brand-gold transition-colors">{sp.name}</span>
                      {b && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${b.color}`}>
                          <Award className="w-2.5 h-2.5 inline mr-0.5" />{b.label}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-brand-muted">
                      {sp.avg_rating > 0 && (
                        <span className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-brand-gold fill-brand-gold" />
                          {sp.avg_rating.toFixed(1)} ({sp.review_count})
                        </span>
                      )}
                      {sp.completed_deals > 0 && (
                        <span className="flex items-center gap-0.5">
                          <Briefcase className="w-3 h-3" />{sp.completed_deals} deals
                        </span>
                      )}
                      {sp.region && (
                        <span className="flex items-center gap-0.5">
                          <MapPin className="w-3 h-3" />{sp.region}
                        </span>
                      )}
                    </div>
                    {/* Match reasons */}
                    {sp.match_reasons?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {sp.match_reasons.slice(0, 3).map((reason, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-brand-gold/5 text-brand-gold/70 border border-brand-gold/10">{reason}</span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Match Score */}
                  <div className="text-center flex-shrink-0 hidden sm:block">
                    <div className="text-brand-gold text-sm font-bold">{sp.score.toFixed(0)}</div>
                    <div className="text-[10px] text-brand-muted">match</div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={(e) => handleShortlist(e, sp)}
                      className="p-1.5 rounded-lg hover:bg-white/5"
                      data-testid="recommended-sp-shortlist"
                    >
                      <Heart className={`w-4 h-4 ${sp.shortlisted ? 'fill-red-500 text-red-500' : 'text-brand-muted hover:text-red-400'}`} />
                    </button>
                    {sp.invited ? (
                      <span className="flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-lg bg-green-600/10 text-green-400 border border-green-600/20" data-testid="recommended-sp-invited">
                        <CheckCircle className="w-3 h-3" /> Invited
                      </span>
                    ) : (
                      <button
                        onClick={(e) => handleInvite(e, sp)}
                        disabled={invitingId === sp.id}
                        data-testid="recommended-sp-invite"
                        className="flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-lg bg-brand-gold/20 text-brand-gold border border-brand-gold/30 hover:bg-brand-gold/30 disabled:opacity-50"
                      >
                        <Send className="w-3 h-3" />
                        Invite
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

export default RecommendedSalespeople;
